import type {Ref} from 'vue';
import {computed} from 'vue';
import {useScreenSizeStore} from '@common/stores';
import pick from 'lodash-es/pick';
import {storeToRefs} from 'pinia';
import type {ExtractAggregationType} from './filters.types';

interface UseFiltersActionsOptions<TFilters extends Record<string, unknown>, TList extends readonly string[]> {
    filters: Ref<TFilters>;
    tempFilters: Ref<TFilters>;
    availableFilters: TList;
    onUpdate: (...args: any) => void;
}

export function useFiltersActions<TFilters extends Record<string, unknown>, TList extends readonly string[]>({
    filters,
    tempFilters,
    availableFilters,
    onUpdate,
}: UseFiltersActionsOptions<TFilters, TList>) {
    const screeSizeStore = useScreenSizeStore();
    const {isSmallScreen} = storeToRefs(screeSizeStore);

    type AvailableSearchFilters = Pick<TFilters, TList[number]>;
    type AvailableAggregations = keyof AvailableSearchFilters;

    const filtersToPick = computed({
        get: () => (isSmallScreen.value ? tempFilters.value : filters.value),
        set(value) {
            if (isSmallScreen.value) {
                tempFilters.value = value;
            } else {
                filters.value = value;
            }
        },
    });

    const filtersFields = computed<AvailableSearchFilters>(() => {
        return pick(filtersToPick.value, availableFilters);
    });

    const isAnyFilterSelected = computed(() => {
        return Object.entries(pick(filtersToPick.value, availableFilters)).some(([_, value]) => {
            if (Array.isArray(value)) {
                return Array.isArray(value) && value.length > 0;
            } else {
                return !!value;
            }
        });
    });

    const selectedFiltersCount = computed(() => {
        return Object.entries(pick(filters.value, availableFilters)).reduce((acc, [_, value]) => {
            if (Array.isArray(value) && value.length) {
                return acc + value.length;
            }
            return acc;
        }, 0);
    });

    function handleResetCategory(...categories: AvailableAggregations[]) {
        categories.forEach((category) => {
            const filterValue = filtersToPick.value[category];
            if (filterValue) {
                if (Array.isArray(filterValue)) {
                    (filtersToPick.value[category] as string[]) = [];
                } else {
                    filtersToPick.value[category] = undefined as any;
                }
                onUpdate();
            }
        });
    }

    function handleUpdateCategory<T extends AvailableAggregations>(category: T, filterValue: ExtractAggregationType<AvailableSearchFilters, T>) {
        let selectedCategory = filtersToPick.value[category];
        if (Array.isArray(selectedCategory) && typeof filterValue === 'string') {
            const existingItem = selectedCategory.findIndex((categ) => categ === filterValue);
            if (existingItem > -1) {
                selectedCategory.splice(existingItem, 1);
            } else {
                (selectedCategory as string[]).push(filterValue);
            }
        } else {
            selectedCategory = filterValue as TFilters[T];
        }
        onUpdate();
    }

    function handleResetAllCategories() {
        filtersToPick.value = {
            ...filtersToPick.value,
            ...Object.fromEntries(
                Object.entries(filtersFields.value).map(([key]) => {
                    return key === 'startDateFrom' || key === 'startDateTo' || key === 'endDateFrom' || key === 'endDateTo'
                        ? [key, undefined]
                        : [key, []];
                }),
            ),
        };
        onUpdate();
    }

    return {
        isAnyFilterSelected,
        selectedFiltersCount,
        handleResetCategory,
        handleUpdateCategory,
        handleResetAllCategories,
    };
}
